import { ofType } from 'redux-observable';
import { type Observable, of } from 'rxjs';
import { filter, mergeMap, withLatestFrom } from 'rxjs/operators';
import gazetteer, { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { toggleQuiz } from '@bridebook/web/lib/quiz/slice';
import {
  type AddedToBookedSuppliersAction,
  type FetchVenueRecommendedSuppliersSuccessAction,
  ShortlistActionTypes,
} from '@bridebook/web/lib/shortlist/action-types';
import { selectIsAnyOnboardingPage } from '@bridebook/web/lib/onboarding/selectors';
import type { IApplicationState, IElasticSupplier, IEpicDeps } from '@bridebook/web/lib/types';
import { setNextAction } from '@bridebook/web/lib/ui/actions';
import { withCurrentWeddingCountry } from '@bridebook/web/lib/utils/operators/with-current-wedding-locale';
import { isArray } from 'lodash';

const shouldDisplayLinkedSupplierQuiz = (
  state: IApplicationState,
  recommendations: IElasticSupplier[],
  weddingCountry: CountryCodes,
) => {
  const market = gazetteer.getMarketByCountry(weddingCountry as CountryCodes);
  const hasSeenRecommendedSuppliers = state.users.user?.actions?.hasSeenRecommendedSuppliers;
  return market.hasFullDirectory && recommendations.length > 0 && !hasSeenRecommendedSuppliers;
};

export const onVenueBookedSetDiscoverPhotographers = (
  action$: Observable<AddedToBookedSuppliersAction>,
  { state$ }: IEpicDeps,
): Observable<any> =>
  action$.pipe(
    ofType(ShortlistActionTypes.ADDED_TO_BOOKED_SUPPLIERS),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const booked = action.payload.item;
      const isVenue = isArray(booked.type)
        ? booked.type.includes('venue')
        : booked.type === 'venue';

      if (isVenue && booked && state.venueConfirmation.triggerLocation === 'countdownWidget') {
        return of(
          setNextAction({
            id: 'venueBooked',
            type: 'venueConfirmation',
            action: 'countdown',
            active: false, // do not show but just preload for better performance
          }),
        );
      }

      return of();
    }),
  );

export const onVenueBookedConfirmedShowDiscoverPhotographers = (
  action$: Observable<FetchVenueRecommendedSuppliersSuccessAction>,
  { state$ }: IEpicDeps,
): Observable<any> =>
  action$.pipe(
    ofType(ShortlistActionTypes.FETCH_VENUE_RECOMMENDED_SUPPLIERS_SUCCESS),
    withCurrentWeddingCountry(state$),
    // Don't interrupt users going through onboarding with the quiz or next action.
    filter(({ state }) => !selectIsAnyOnboardingPage(state)),
    mergeMap(({ state, action, weddingCountry }) => {
      if (shouldDisplayLinkedSupplierQuiz(state, action.payload, weddingCountry)) {
        return of(toggleQuiz({ open: true, quizId: 'atYourVenue', quizSource: 'multiEnquiry' }));
      }

      return of();
    }),
  );
